import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface ForexRateResponse {
  date: string;
  base: string;
  quote: string;
  rate: number;
  marketRate?: number;
  centralBankRate?: number;
  provider: string;
  centralBankProvider?: string;
  source?: string;
  fallback?: boolean;
}

export interface ForexRatesItem {
  date: string;
  base: string;
  quote: string;
  rate: number;
  marketRate?: number;
  centralBankRate?: number;
}

export interface ForexRatesResponse {
  date: string;
  base: string;
  rates: ForexRatesItem[];
  provider: string;
  fallback?: boolean;
}

export interface ForexCurrencyItem {
  code: string;
  name: string;
  symbol: string;
}

export interface ForexCurrenciesResponse {
  currencies: Record<string, string>;
  popular: ForexCurrencyItem[];
  provider: string;
}

export function useForexRate(
  base: string,
  quote: string,
  source: 'market' | 'central_bank' = 'market',
  enabled: boolean = true
) {
  return useQuery<ForexRateResponse>({
    queryKey: ['forex', 'rate', base, quote, source],
    queryFn: async () => {
      const res = await api.get<ForexRateResponse>(
        `/api/forex/rate/${base.toUpperCase()}/${quote.toUpperCase()}`,
        { params: { source } }
      );
      return res.data;
    },
    enabled: enabled && !!base && !!quote,
    staleTime: 1000 * 60 * 3, // 3 minutes cache for live market
    refetchOnWindowFocus: false,
  });
}

export function useForexRates(base: string = 'INR', quotes: string = 'USD,EUR,AED,GBP,SAR,CAD,SGD,JPY') {
  return useQuery<ForexRatesResponse>({
    queryKey: ['forex', 'rates', base, quotes],
    queryFn: async () => {
      const res = await api.get<ForexRatesResponse>('/api/forex/rates', {
        params: { base: base.toUpperCase(), quotes: quotes.toUpperCase() },
      });
      return res.data;
    },
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
  });
}

export function useForexCurrencies() {
  return useQuery<ForexCurrenciesResponse>({
    queryKey: ['forex', 'currencies'],
    queryFn: async () => {
      const res = await api.get<ForexCurrenciesResponse>('/api/forex/currencies');
      return res.data;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  });
}
